import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { toast } from "sonner";
import { delftBlue } from "@/theme/colors";
import { useAssignPrestartFormCompaniesMutation,
  useGetCompaniesQuery,
  useGetPrestartFormVersionsQuery,
  usePublishPrestartFormMutation,
  useRestorePrestartFormVersionMutation,
  useDeletePrestartFormVersionMutation,
  useUnassignPrestartFormCompaniesMutation,
  useUpdatePrestartFormMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
import FormBuilder from "./FormBuilder";

const entityId = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  return String(item.id || item._id || "");
};

const companyName = (item) => {
  if (!item) return "Unknown";
  if (typeof item === "string") return item;
  return item.name || "Unknown";
};

const isGenericVersionNote = (note) => {
  const text = String(note || "").trim();
  return !text || /^Version \d+$/i.test(text) || /^Restored from version \d+/i.test(text);
};

const versionLabel = (row, latestVersion) => {
  if (!row) return "Unnamed";
  if (row.versionName?.trim()) return row.versionName.trim();
  if (!isGenericVersionNote(row.changeNote)) return String(row.changeNote).trim();
  if (Number(row.version) === Number(latestVersion)) return "Latest";
  return "Unnamed";
};

const PrestartFormVersionPanel = ({
  formId,
  form,
  isSuperAdmin,
  selectedVersion,
  onSelectVersion,
}) => {
  const latestVersion = form?.latestVersion || form?.templateVersion || 1;
  const viewingVersion =
    selectedVersion === "latest" || !selectedVersion
      ? form?.templateVersion || latestVersion
      : selectedVersion;
  const isLatest =
    selectedVersion === "latest" ||
    !selectedVersion ||
    Number(viewingVersion) === Number(latestVersion);

  const { data: versionsResponse, isLoading: isLoadingVersions } = useGetPrestartFormVersionsQuery(formId, {
    skip: !formId || !isSuperAdmin,
  });
  const { data: companiesData } = useGetCompaniesQuery({ page: 1, limit: 10000 }, { skip: !isSuperAdmin });

  const [updatePrestartForm, { isLoading: isSaving }] = useUpdatePrestartFormMutation();
  const [publishPrestartForm, { isLoading: isPublishing }] = usePublishPrestartFormMutation();
  const [assignCompanies, { isLoading: isAssigning }] = useAssignPrestartFormCompaniesMutation();
  const [unassignCompanies, { isLoading: isUnassigning }] = useUnassignPrestartFormCompaniesMutation();
  const [restoreVersion, { isLoading: isRestoring }] = useRestorePrestartFormVersionMutation();
  const [deleteVersion, { isLoading: isDeletingVersion }] = useDeletePrestartFormVersionMutation();

  const [formConfig, setFormConfig] = useState(form?.formConfig || {});
  const [versionName, setVersionName] = useState("");
  const [versionTitle, setVersionTitle] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [addCompaniesOpen, setAddCompaniesOpen] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [draftCompanyIds, setDraftCompanyIds] = useState([]);

  useEffect(() => {
    setFormConfig(form?.formConfig || {});
  }, [form?.formConfig, form?.templateVersion]);

  const versionsPayload = versionsResponse?.data?.data || versionsResponse?.data || {};
  const versions = versionsPayload.versions || [];
  const assignments = versionsPayload.assignments || [];
  const versionLabelByNumber = useMemo(() => {
    const labels = {};
    versions.forEach((row) => {
      labels[Number(row.version)] = versionLabel(row, latestVersion);
    });
    return labels;
  }, [versions, latestVersion]);
  const viewingVersionRow = versions.find((row) => Number(row.version) === Number(viewingVersion));
  const activityName = form?.activityId?.name?.trim() || "";
  const savedVersionTitle = viewingVersionRow?.versionTitle?.trim() || "";
  const defaultVersionTitle = savedVersionTitle || activityName;
  const viewingVersionLabel = versionLabelByNumber[Number(viewingVersion)] || "Unnamed";

  useEffect(() => {
    setVersionTitle(savedVersionTitle);
    setIsEditingTitle(false);
  }, [formId, viewingVersion, savedVersionTitle]);
  const companies = companiesData?.data?.companies || companiesData?.companies || [];

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        id: entityId(company),
        name: company.name || "Unknown Company",
      })),
    [companies]
  );

  const assignedCompanyIds = useMemo(
    () => assignments.map((row) => entityId(row.companyId)).filter(Boolean),
    [assignments]
  );
  const companyNameById = useMemo(() => {
    const names = {};
    companyOptions.forEach((company) => {
      names[company.id] = company.name;
    });
    (Array.isArray(form?.companyIds) ? form.companyIds : []).forEach((company) => {
      const id = entityId(company);
      if (id) names[id] = companyName(company);
    });
    return names;
  }, [companyOptions, form?.companyIds]);

  const companiesOnViewedVersion = useMemo(
    () =>
      assignments
        .filter((row) => Number(row.publishedVersion) === Number(viewingVersion))
        .map((row) => {
          const id = entityId(row.companyId);
          return { id, name: companyNameById[id] || companyName(row.companyId) };
        })
        .filter((row) => row.id),
    [assignments, viewingVersion, companyNameById]
  );
  const companiesOnViewedVersionIds = useMemo(
    () => companiesOnViewedVersion.map((row) => row.id),
    [companiesOnViewedVersion]
  );
  const savedCompanyKey = companiesOnViewedVersionIds.slice().sort().join(",");

  useEffect(() => {
    setDraftCompanyIds(savedCompanyKey ? savedCompanyKey.split(",") : []);
  }, [formId, viewingVersion, savedCompanyKey]);

  const draftCompanies = useMemo(
    () =>
      draftCompanyIds.map((id) => ({
        id,
        name: companyNameById[id] || id,
      })),
    [draftCompanyIds, companyNameById]
  );
  const companiesNotOnThisVersion = useMemo(
    () =>
      companyOptions
        .filter((company) => !draftCompanyIds.includes(company.id))
        .map((company) => {
          const assignment = assignments.find((row) => entityId(row.companyId) === company.id);
          return {
            ...company,
            currentVersion: assignment ? Number(assignment.publishedVersion) : null,
            currentVersionLabel: assignment
              ? versionLabelByNumber[Number(assignment.publishedVersion)] || "Unnamed"
              : null,
          };
        }),
    [companyOptions, draftCompanyIds, assignments, versionLabelByNumber]
  );

  const companiesOnLatest = assignments.some(
    (row) => Number(row.publishedVersion) === Number(latestVersion)
  );
  const isDraftLatest = isLatest && !companiesOnLatest;
  const publishCompanyIds = isDraftLatest ? draftCompanyIds : companiesOnViewedVersionIds;
  const publishCompanies = publishCompanyIds.map((id) => ({
    id,
    name: companyNameById[id] || id,
  }));
  const hasConfigChanges = useMemo(
    () => JSON.stringify(formConfig || {}) !== JSON.stringify(form?.formConfig || {}),
    [formConfig, form?.formConfig]
  );
  const hasCompanyChanges = useMemo(
    () => draftCompanyIds.slice().sort().join(",") !== savedCompanyKey,
    [draftCompanyIds, savedCompanyKey]
  );
  const hasTitleChanges = (versionTitle.trim() || activityName) !== defaultVersionTitle;
  const hasUnsavedChanges = hasConfigChanges || hasCompanyChanges || hasTitleChanges;
  const isPersisting = isSaving || isAssigning || isUnassigning;
  const showSaveTemplate = isDraftLatest;
  const showUpdateTemplate = !isDraftLatest;
  const showPublish = isDraftLatest && !hasConfigChanges && !hasTitleChanges;
  const hasCompaniesOnViewedVersion = companiesOnViewedVersion.length > 0;
  const canDeleteVersion = isSuperAdmin && versions.length > 1 && !hasCompaniesOnViewedVersion;
  const versionStatusText = isDraftLatest
    ? "Unpublished draft"
    : assignedCompanyIds.length === 0
      ? "Draft (no companies assigned)"
      : "";

  const handleSaveConfig = async () => {
    if (hasConfigChanges && (!formConfig || typeof formConfig !== "object")) {
      toast.error("Form template is invalid");
      return;
    }
    if (isDraftLatest && !hasConfigChanges && !hasTitleChanges) {
      return;
    }
    if (!hasUnsavedChanges) {
      return;
    }
    const versionToSync = Number(viewingVersion);
    try {
      if (hasConfigChanges || hasTitleChanges || versionName.trim()) {
        await updatePrestartForm({
          id: formId,
          ...(hasConfigChanges ? { formConfig } : {}),
          ...(versionName.trim() ? { versionName: versionName.trim() } : {}),
          ...(hasTitleChanges ? { versionTitle: versionTitle.trim() } : {}),
          version: versionToSync,
        }).unwrap();
      }
      if (hasCompanyChanges && !isDraftLatest) {
        const saved = new Set(companiesOnViewedVersionIds);
        const draft = new Set(draftCompanyIds);
        const toAdd = draftCompanyIds.filter((id) => !saved.has(id));
        const toRemove = companiesOnViewedVersionIds.filter((id) => !draft.has(id));
        if (toAdd.length) {
          await assignCompanies({
            id: formId,
            companyIds: toAdd,
            version: versionToSync,
          }).unwrap();
        }
        if (toRemove.length) {
          await unassignCompanies({ id: formId, companyIds: toRemove }).unwrap();
        }
      }
      toast.success(
        isDraftLatest
          ? "Draft saved. Publish it to make this version live for companies."
          : hasConfigChanges
            ? "Template updated"
            : hasTitleChanges
              ? "Form version title saved"
              : "Company changes saved"
      );
      setVersionName("");
      setSaveOpen(false);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save template");
    }
  };

  const handlePublish = async () => {
    if (!publishCompanyIds.length) {
      toast.error("Add companies with + before publishing this version");
      return;
    }
    try {
      await publishPrestartForm({
        id: formId,
        version: Number(viewingVersion),
        companyIds: publishCompanyIds,
      }).unwrap();
      toast.success(`Published to listed companies`);
      setPublishOpen(false);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to publish");
    }
  };

  const handleAddCompanies = () => {
    if (!selectedCompanyIds.length) {
      toast.error("Select at least one company");
      return;
    }
    setDraftCompanyIds((current) => [...new Set([...current, ...selectedCompanyIds])]);
    setAddCompaniesOpen(false);
    setSelectedCompanyIds([]);
  };

  const handleRemoveCompany = (companyId) => {
    const name = draftCompanies.find((company) => company.id === companyId)?.name || companyId;
    if (
      !window.confirm(
        `Remove ${name} from this version? ${isDraftLatest ? "Publish to apply this change." : "Update to apply this change."}`
      )
    ) {
      return;
    }
    setDraftCompanyIds((current) => current.filter((id) => id !== companyId));
  };

  const handleRestore = async () => {
    if (!window.confirm(`Duplicate ${viewingVersionLabel} as a new draft? It will not be saved as live until you save and publish.`)) {
      return;
    }
    try {
      const result = await restoreVersion({ id: formId, version: viewingVersion }).unwrap();
      toast.success(result?.message || "Duplicated as a draft. Edit, then save and publish when ready.");
      onSelectVersion("latest");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to duplicate version");
    }
  };

  const handleDeleteVersion = async () => {
    if (!canDeleteVersion) return;
    if (
      !window.confirm(
        `Delete ${viewingVersionLabel}? This cannot be undone from the version list. Companies are not assigned to this version.`
      )
    ) {
      return;
    }
    try {
      await deleteVersion({ id: formId, version: viewingVersion }).unwrap();
      toast.success(`${viewingVersionLabel} deleted`);
      onSelectVersion("latest");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete version");
    }
  };

  return (
    <PaperSection>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isSuperAdmin && isEditingTitle ? (
              <TextField
                size="small"
                label="Form version title"
                placeholder={activityName || "Give this version a custom title"}
                value={versionTitle}
                onChange={(e) => setVersionTitle(e.target.value)}
                onBlur={() => setIsEditingTitle(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setIsEditingTitle(false);
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setVersionTitle(savedVersionTitle);
                    setIsEditingTitle(false);
                  }
                }}
                autoFocus
                inputProps={{ maxLength: 150 }}
                sx={{
                  width: { xs: "100%", sm: 320 },
                  "& .MuiInputBase-root": { height: 40 },
                }}
              />
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minHeight: 40 }}>
                <Typography
                  sx={{
                    color: (versionTitle.trim() || activityName) ? delftBlue[500] : delftBlue[300],
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  {versionTitle.trim() || activityName || "Form version title"}
                </Typography>
                {isSuperAdmin && (
                  <Tooltip title="Edit form version title">
                    <IconButton
                      size="small"
                      aria-label="Edit form version title"
                      onClick={() => {
                        if (!versionTitle.trim() && activityName) {
                          setVersionTitle(activityName);
                        }
                        setIsEditingTitle(true);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
            {versionStatusText ? (
              <Typography sx={{ color: delftBlue[400], fontFamily: "Montserrat, sans-serif", fontSize: 13, mt: 0.5 }}>
                {versionStatusText}
              </Typography>
            ) : null}
          </Box>
          <FormControl size="small" sx={{ minWidth: 160, "& .MuiInputBase-root": { height: 40 } }}>
            <InputLabel>Version</InputLabel>
            <Select
              label="Version"
              value={
                selectedVersion === "latest" || !selectedVersion
                  ? String(latestVersion)
                  : String(selectedVersion)
              }
              onChange={(e) => {
                const version = Number(e.target.value);
                onSelectVersion(version === Number(latestVersion) ? "latest" : version);
              }}
            >
              {versions.map((row) => (
                <MenuItem key={row.version} value={String(row.version)}>
                  {versionLabel(row, latestVersion)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {isLoadingVersions ? (
          <CircularProgress size={24} />
        ) : (
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ color: delftBlue[400], fontSize: 12, fontFamily: "Montserrat, sans-serif" }}>
                Companies on this version
              </Typography>
              {isSuperAdmin && (
                <Tooltip title="Add companies not on this version">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Add companies"
                      onClick={() => {
                        setSelectedCompanyIds([]);
                        setAddCompaniesOpen(true);
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {draftCompanies.length ? (
                draftCompanies.map((company) => (
                  <Chip
                    key={company.id}
                    label={company.name}
                    size="small"
                    onDelete={isSuperAdmin && company.id ? () => handleRemoveCompany(company.id) : undefined}
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  />
                ))
              ) : (
                <Typography sx={{ color: delftBlue[400], fontSize: 13, fontFamily: "Montserrat, sans-serif" }}>
                  None
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {isSuperAdmin && (
          <>
            {!isLatest && (
              <Alert severity="info">
                You are viewing an older version. Update saves this version in place for companies already on it.
                Duplicate it if you want a new draft copy to save and publish.
              </Alert>
            )}
            <Typography sx={{ fontFamily: "Audiowide, sans-serif", color: delftBlue[500], fontSize: 16 }}>
              Form builder
            </Typography>
            <FormBuilder value={formConfig} onChange={setFormConfig} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {showSaveTemplate && (
                <Button
                  variant="contained"
                  disabled={(!hasConfigChanges && !hasTitleChanges) || isPersisting}
                  onClick={() => setSaveOpen(true)}
                  sx={{ backgroundColor: delftBlue[500], textTransform: "none" }}
                >
                  Save template
                </Button>
              )}
              {showUpdateTemplate && (
                <Button
                  variant="contained"
                  disabled={!hasUnsavedChanges || isPersisting}
                  onClick={() => setSaveOpen(true)}
                  sx={{ backgroundColor: delftBlue[500], textTransform: "none" }}
                >
                  Update template
                </Button>
              )}
              {showPublish && (
                <Button variant="outlined" onClick={() => setPublishOpen(true)} sx={{ textTransform: "none" }}>
                  Publish
                </Button>
              )}
              <Button variant="outlined" disabled={isRestoring} onClick={handleRestore} sx={{ textTransform: "none" }}>
                {isRestoring ? <CircularProgress size={20} /> : "Duplicate"}
              </Button>
              {canDeleteVersion && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isDeletingVersion}
                  onClick={handleDeleteVersion}
                  sx={{ textTransform: "none" }}
                >
                  {isDeletingVersion ? <CircularProgress size={20} /> : "Delete"}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Stack>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{showUpdateTemplate ? "Update form" : "Save template"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 14 }}>
              {showUpdateTemplate
                ? "This will save your changes in place. Companies on this form will see the update. Do you want to continue?"
                : "This saves the draft template only. It will not go live until you publish."}
            </Typography>
            <TextField
              label="Version name"
              placeholder="e.g. Summer safety update"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 100 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveConfig}
            disabled={(showUpdateTemplate ? !hasUnsavedChanges : !hasConfigChanges && !hasTitleChanges) || isPersisting}
            variant="contained"
          >
            {isPersisting ? <CircularProgress size={20} /> : showUpdateTemplate ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Publish</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 14 }}>
              These companies will start using {viewingVersionLabel} after you publish. This draft is not live yet.
            </Typography>
            {publishCompanies.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {publishCompanies.map((company) => (
                  <Chip
                    key={company.id}
                    label={company.name}
                    size="small"
                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                  />
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No companies on this version yet. Use + to add companies first.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} disabled={isPublishing || !publishCompanies.length} variant="contained">
            {isPublishing ? <CircularProgress size={20} /> : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addCompaniesOpen} onClose={() => setAddCompaniesOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add companies</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 14 }}>
              Showing companies that are not on this version.
            </Typography>
            <CompanyChecklist
              options={companiesNotOnThisVersion}
              selected={selectedCompanyIds}
              onChange={setSelectedCompanyIds}
              emptyMessage="All companies are already on this version"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCompaniesOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCompanies} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </PaperSection>
  );
};

const PaperSection = ({ children }) => (
  <Box
    sx={{
      padding: { xs: 2, sm: 3 },
      borderRadius: 2,
      outline: `1px ${delftBlue[100]} solid`,
      background: "white",
    }}
  >
    {children}
  </Box>
);

const CompanyChecklist = ({ options, selected, onChange, emptyMessage = "No companies found" }) => {
  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  if (!options.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <Stack spacing={0.5} sx={{ maxHeight: 280, overflowY: "auto" }}>
      {options.map((option) => (
        <Stack key={option.id} direction="row" spacing={1} alignItems="center">
          <Checkbox checked={selected.includes(option.id)} onChange={() => toggle(option.id)} />
          <Box>
            <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 14 }}>{option.name}</Typography>
            {option.currentVersionLabel ? (
              <Typography sx={{ fontFamily: "Montserrat, sans-serif", fontSize: 12, color: delftBlue[400] }}>
                Currently on {option.currentVersionLabel}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default PrestartFormVersionPanel;
